<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <div><h4 class="st-page-title"><i class="mdi mdi-map-marker-radius me-2"></i>Attendance & GPS Tracking</h4></div>
        </div>
        <div class="d-flex gap-2">
          <button class="st-btn st-btn-blue" @click="exportCSV"><i class="mdi mdi-download me-1"></i>Export CSV</button>
          <button class="st-btn" style="background:rgba(239,68,68,.12);color:#EF4444;border:1px solid rgba(239,68,68,.3);" @click="exportPDF"><i class="mdi mdi-file-pdf-box me-1"></i>PDF Report</button>
        </div>
      </div>

      <!-- FILTERS -->
      <div class="st-card st-filter-card">
        <div class="row g-2 align-items-end">
          <!-- Period Presets -->
          <div class="col-auto">
            <label class="st-label">Quick Period</label>
            <div class="d-flex gap-1 flex-wrap">
              <button class="st-pill" :class="{active:periodPreset==='today'}" @click="setPeriod('today')">Today</button>
              <button class="st-pill" :class="{active:periodPreset==='yesterday'}" @click="setPeriod('yesterday')">Yesterday</button>
              <button class="st-pill" :class="{active:periodPreset==='this_week'}" @click="setPeriod('this_week')">This Week</button>
              <button class="st-pill" :class="{active:periodPreset==='this_month'}" @click="setPeriod('this_month')">This Month</button>
              <button class="st-pill" :class="{active:periodPreset==='custom'}" @click="periodPreset='custom'">Custom</button>
            </div>
          </div>
          <!-- Date Range -->
          <div class="col-auto" v-if="periodPreset==='custom'">
            <label class="st-label">From</label>
            <input type="date" class="st-input st-date-input" v-model="dateFrom" style="width:auto;padding:6px 10px;font-size:12px;" />
          </div>
          <div class="col-auto" v-if="periodPreset==='custom'">
            <label class="st-label">To</label>
            <input type="date" class="st-input st-date-input" v-model="dateTo" style="width:auto;padding:6px 10px;font-size:12px;" />
          </div>
          <!-- Class Filter -->
          <div class="col-auto">
            <label class="st-label">Class</label>
            <select class="st-select" v-model="selectedClass" style="width:auto;padding:6px 10px;font-size:12px;">
              <option value="">All Classes</option>
              <option v-for="c in classes" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
          <!-- Tap Point Type -->
          <div class="col-auto">
            <label class="st-label">Tap Type</label>
            <select class="st-select" v-model="selectedTapType" style="width:auto;padding:6px 10px;font-size:12px;">
              <option value="">All Types</option>
              <option value="gate_entry">Gate Entry</option>
              <option value="gate_exit">Gate Exit</option>
              <option value="classroom">Classroom</option>
              <option value="bus_boarding">Bus Boarding</option>
              <option value="bus_dropoff">Bus Drop-off</option>
              <option value="event">Event</option>
            </select>
          </div>
          <!-- Search -->
          <div class="col-auto">
            <label class="st-label">Student</label>
            <input class="st-input" v-model="search" placeholder="Search name..." style="width:160px;padding:6px 10px;font-size:12px;" />
          </div>
          <!-- Apply -->
          <div class="col-auto">
            <button class="st-btn st-btn-blue" @click="loadAttendance" style="padding:7px 16px;"><i class="mdi mdi-magnify me-1"></i>Apply</button>
          </div>
        </div>
      </div>

      <!-- Stats -->
      <div class="st-stat-row">
        <div class="st-stat">
          <div class="st-stat-num" style="color:#10B981;">{{ presentCount }}</div>
          <div class="st-stat-lbl">Present</div>
        </div>
        <div class="st-stat">
          <div class="st-stat-num" style="color:#EF4444;">{{ absentCount }}</div>
          <div class="st-stat-lbl">Absent</div>
        </div>
        <div class="st-stat">
          <div class="st-stat-num">{{ totalTaps }}</div>
          <div class="st-stat-lbl">Total Taps</div>
        </div>
        <div class="st-stat">
          <div class="st-stat-num" style="color:#F59E0B;">{{ attendanceRate }}%</div>
          <div class="st-stat-lbl">Attendance Rate</div>
        </div>
        <div class="st-stat">
          <div class="st-stat-num" style="color:#8BA4C4;">{{ periodLabel }}</div>
          <div class="st-stat-lbl">Period</div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="st-tabs">
        <button class="st-tab" :class="{active:tab==='map'}" @click="tab='map';$nextTick(()=>initMap())"><i class="mdi mdi-map me-1"></i>GPS Map</button>
        <button class="st-tab" :class="{active:tab==='list'}" @click="tab='list'"><i class="mdi mdi-format-list-bulleted me-1"></i>Student List</button>
        <button class="st-tab" :class="{active:tab==='timeline'}" @click="tab='timeline'"><i class="mdi mdi-timeline-clock me-1"></i>Timeline</button>
      </div>

      <!-- MAP TAB -->
      <div class="st-card" v-show="tab==='map'">
        <div class="st-card-title d-flex justify-content-between align-items-center">
          <span><i class="mdi mdi-map-marker me-1"></i>Live Attendance Map</span>
          <span class="st-badge st-badge-info">{{ mapMarkers.length }} taps with GPS</span>
        </div>
        <div id="attendanceMap" class="st-map"></div>
        <div class="st-map-legend mt-2">
          <span class="st-legend-item"><span class="st-legend-dot" style="background:#10B981;"></span>Gate Entry</span>
          <span class="st-legend-item"><span class="st-legend-dot" style="background:#2E86DE;"></span>Classroom</span>
          <span class="st-legend-item"><span class="st-legend-dot" style="background:#F59E0B;"></span>Bus</span>
          <span class="st-legend-item"><span class="st-legend-dot" style="background:#EF4444;"></span>Gate Exit</span>
          <span class="st-legend-item"><span class="st-legend-dot" style="background:#A855F7;"></span>Event</span>
        </div>
      </div>

      <!-- LIST TAB -->
      <div class="st-card" v-if="tab==='list'">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <div class="st-search-wrap"><i class="mdi mdi-magnify"></i><input class="st-search" v-model="search" placeholder="Search student..." /></div>
          <div class="d-flex gap-2">
            <button class="st-btn st-btn-sm" :class="filterPresence==='all'?'st-btn-blue':''" @click="filterPresence='all'">All</button>
            <button class="st-btn st-btn-sm" :class="filterPresence==='present'?'st-btn-green':''" @click="filterPresence='present'" style="color:#10B981;">Present</button>
            <button class="st-btn st-btn-sm" :class="filterPresence==='absent'?'st-btn-red':''" @click="filterPresence='absent'" style="color:#EF4444;">Absent</button>
          </div>
        </div>
        <div v-if="loading" class="st-spinner"><div class="spinner-border"></div></div>
        <div v-else-if="filteredStudents.length" style="overflow-x:auto;">
          <table class="st-table">
            <thead><tr><th>Student</th><th>Class</th><th>Status</th><th>Taps</th><th>Details</th><th>Actions</th></tr></thead>
            <tbody>
            <tr v-for="s in filteredStudents" :key="s.id">
              <td>
                <div style="font-weight:600;">{{ s.name }}</div>
                <div style="color:#8BA4C4;font-size:11px;">{{ s.admission_no }}</div>
              </td>
              <td>{{ s.class_name || '-' }}</td>
              <td>
                <span class="st-badge" :class="s.present?'st-badge-success':'st-badge-danger'">{{ s.present ? 'Present' : 'Absent' }}</span>
              </td>
              <td>{{ s.taps ? s.taps.length : 0 }}</td>
              <td>
                <div v-if="s.taps && s.taps.length">
                  <div v-for="(tap, ti) in (s._expanded ? s.taps : s.taps.slice(0, 2))" :key="ti" style="font-size:12px;margin-bottom:3px;">
                    <span class="st-tap-dot" :style="{background: tapColor(tap.tap_point_type)}"></span>
                    <strong>{{ formatTime(tap.tapped_at) }}</strong>
                    <span style="color:#8BA4C4;"> — {{ tapLabel(tap.tap_point_type) }}</span>
                    <span v-if="tap.tap_point_name" style="color:#4A6B8A;"> · {{ tap.tap_point_name }}</span>
                  </div>
                  <button v-if="s.taps.length > 2" class="st-view-more" @click="s._expanded = !s._expanded; $forceUpdate()">
                    {{ s._expanded ? '▲ Show less' : '▼ ' + (s.taps.length - 2) + ' more tap' + (s.taps.length - 2 > 1 ? 's' : '') }}
                  </button>
                </div>
                <span v-else style="color:#4A6B8A;">No taps</span>
              </td>
              <td>
                <div class="d-flex gap-1">
                  <button v-if="s.taps && s.taps.length" class="st-btn st-btn-sm st-btn-blue" @click="showOnMap(s)" title="View on map">
                    <i class="mdi mdi-map-marker"></i>
                  </button>
                  <button v-if="s.taps && s.taps.length && getLastGps(s)" class="st-btn st-btn-sm" style="background:rgba(16,185,129,.15);color:#10B981;border:1px solid rgba(16,185,129,.3);" @click="openGps(getLastGps(s).lat, getLastGps(s).lng)" title="Open GPS">
                    <i class="mdi mdi-crosshairs-gps"></i>
                  </button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-account-school"></i>No students found</div>
      </div>

      <!-- TIMELINE TAB -->
      <div class="st-card" v-if="tab==='timeline'">
        <div class="st-card-title">Tap Timeline — {{ selectedDate }}</div>
        <div v-if="allTaps.length">
          <div class="st-timeline-item" v-for="(tap, ti) in allTaps" :key="ti">
            <div class="st-timeline-dot" :style="{background: tapColor(tap.tap_point_type)}"></div>
            <div class="st-timeline-line" v-if="ti < allTaps.length - 1"></div>
            <div class="st-timeline-content">
              <div class="st-timeline-time">{{ formatTime(tap.tapped_at) }}</div>
              <div class="st-timeline-name">{{ tap.student_name || 'Student' }}</div>
              <div class="st-timeline-type">
                <span class="st-badge" :style="{background: tapColor(tap.tap_point_type)+'26', color: tapColor(tap.tap_point_type)}">{{ tapLabel(tap.tap_point_type) }}</span>
                <span v-if="tap.gps_lat" class="st-timeline-gps" @click="openGps(tap.gps_lat, tap.gps_lng)"><i class="mdi mdi-map-marker me-1"></i>GPS</span>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="st-empty"><i class="mdi mdi-timeline-clock"></i>No taps recorded for this date</div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";

export default {
  name: "AttendanceDashboard",
  components: { Navigation },
  data() {
    return {
      loading: true,
      tab: 'list',
      periodPreset: 'today',
      dateFrom: new Date().toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0],
      selectedDate: new Date().toISOString().split('T')[0],
      selectedClass: '',
      selectedTapType: '',
      classes: [],
      students: [],
      search: '',
      filterPresence: 'all',
      map: null,
      markersLayer: null,
      mapInitialized: false,
    };
  },
  computed: {
    presentCount() { return this.students.filter(s => s.present).length; },
    absentCount() { return this.students.filter(s => !s.present).length; },
    totalTaps() { return this.students.reduce((sum, s) => sum + (s.taps ? s.taps.length : 0), 0); },
    attendanceRate() {
      if (!this.students.length) return 0;
      return Math.round((this.presentCount / this.students.length) * 100);
    },
    periodLabel() {
      if (this.periodPreset === 'today') return 'Today';
      if (this.periodPreset === 'yesterday') return 'Yesterday';
      if (this.periodPreset === 'this_week') return 'This Week';
      if (this.periodPreset === 'this_month') return 'This Month';
      return this.dateFrom + ' → ' + this.dateTo;
    },
    filteredStudents() {
      var list = this.students;
      if (this.filterPresence === 'present') list = list.filter(s => s.present);
      if (this.filterPresence === 'absent') list = list.filter(s => !s.present);
      if (this.search) {
        var q = this.search.toLowerCase();
        list = list.filter(s => (s.name || '').toLowerCase().includes(q) || (s.admission_no || '').includes(q));
      }
      if (this.selectedTapType) {
        var tt = this.selectedTapType;
        list = list.filter(s => s.taps && s.taps.some(t => t.tap_point_type === tt));
      }
      return list;
    },
    mapMarkers() {
      var markers = [];
      this.students.forEach(s => {
        if (s.taps) {
          s.taps.forEach(t => {
            if (t.gps_lat && t.gps_lng) {
              markers.push({ lat: parseFloat(t.gps_lat), lng: parseFloat(t.gps_lng), name: s.name, type: t.tap_point_type, time: t.tapped_at, admNo: s.admission_no });
            }
          });
        }
      });
      return markers;
    },
    allTaps() {
      var taps = [];
      this.students.forEach(s => {
        if (s.taps) {
          s.taps.forEach(t => {
            taps.push({ ...t, student_name: s.name, admission_no: s.admission_no });
          });
        }
      });
      taps.sort((a, b) => new Date(b.tapped_at) - new Date(a.tapped_at));
      return taps;
    }
  },
  mounted() {
    this.loadClasses();
    this.loadAttendance();
    this.loadLeaflet();
  },
  methods: {
    loadLeaflet() {
      // Load Leaflet CSS
      if (!document.getElementById('leaflet-css')) {
        var link = document.createElement('link');
        link.id = 'leaflet-css';
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);
      }
      // Load Leaflet JS
      if (!window.L) {
        var script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => { this.$nextTick(() => this.initMap()); };
        document.head.appendChild(script);
      }
    },

    initMap() {
      if (!window.L || this.mapInitialized) return;
      var mapEl = document.getElementById('attendanceMap');
      if (!mapEl) return;

      this.map = L.map('attendanceMap').setView([-1.2921, 36.8219], 13); // Default: Nairobi
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19
      }).addTo(this.map);

      this.markersLayer = L.layerGroup().addTo(this.map);
      this.mapInitialized = true;
      this.updateMapMarkers();
    },

    updateMapMarkers() {
      if (!this.map || !this.markersLayer) return;
      this.markersLayer.clearLayers();

      var bounds = [];
      this.mapMarkers.forEach(m => {
        var color = this.tapColor(m.type);
        var icon = L.divIcon({
          className: 'st-map-marker-custom',
          html: '<div style="width:14px;height:14px;border-radius:50%;background:' + color + ';border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4);"></div>',
          iconSize: [14, 14],
          iconAnchor: [7, 7]
        });
        var marker = L.marker([m.lat, m.lng], { icon: icon });
        marker.bindPopup(
            '<div style="font-size:13px;min-width:160px;">' +
            '<strong>' + m.name + '</strong><br/>' +
            '<span style="color:#666;">' + m.admNo + '</span><br/>' +
            '<span style="background:' + color + '22;color:' + color + ';padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;">' + this.tapLabel(m.type) + '</span><br/>' +
            '<span style="color:#999;font-size:11px;">' + this.formatTime(m.time) + '</span>' +
            '</div>'
        );
        this.markersLayer.addLayer(marker);
        bounds.push([m.lat, m.lng]);
      });

      if (bounds.length) {
        this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 });
      }
    },

    async loadClasses() {
      try {
        var r = await executeJson({ function: 'getClasses' }, simitrack);
        if (r.data.success) this.classes = Array.isArray(r.data.data) ? r.data.data : [];
      } catch (e) {}
    },

    async loadAttendance() {
      this.loading = true;
      try {
        var params = { function: 'getClassAttendance', date: this.dateFrom, dateTo: this.dateTo };
        if (this.selectedClass) params.classId = this.selectedClass;
        if (this.selectedTapType) params.tapType = this.selectedTapType;
        var r = await executeJson(params, simitrack);
        if (r.data.success) {
          this.students = r.data.data.students || [];
        }
      } catch (e) { console.error(e); }
      this.loading = false;
      this.$nextTick(() => this.updateMapMarkers());
    },

    setPeriod(preset) {
      this.periodPreset = preset;
      var today = new Date();
      var fmt = (d) => d.toISOString().split('T')[0];
      if (preset === 'today') {
        this.dateFrom = fmt(today); this.dateTo = fmt(today);
      } else if (preset === 'yesterday') {
        var y = new Date(today); y.setDate(y.getDate() - 1);
        this.dateFrom = fmt(y); this.dateTo = fmt(y);
      } else if (preset === 'this_week') {
        var monday = new Date(today); monday.setDate(monday.getDate() - monday.getDay() + 1);
        this.dateFrom = fmt(monday); this.dateTo = fmt(today);
      } else if (preset === 'this_month') {
        var first = new Date(today.getFullYear(), today.getMonth(), 1);
        this.dateFrom = fmt(first); this.dateTo = fmt(today);
      }
      this.selectedDate = this.dateFrom;
      this.loadAttendance();
    },

    exportCSV() {
      var school = sessionStorage.getItem('institutionName') || 'School';
      var rows = [
        [school + ' — Attendance Report'],
        ['Generated: ' + new Date().toLocaleString()],
        ['Period: ' + this.periodLabel + ' (' + this.dateFrom + (this.dateFrom !== this.dateTo ? ' to ' + this.dateTo : '') + ')'],
        ['Present: ' + this.presentCount, 'Absent: ' + this.absentCount, 'Total Students: ' + this.students.length, 'Rate: ' + this.attendanceRate + '%'],
        [],
        ['#', 'Student Name', 'Admission No', 'Class', 'Status', 'Total Taps', 'First Tap', 'Last Tap', 'Tap Details']
      ];
      this.filteredStudents.forEach((s, i) => {
        var firstTap = s.taps && s.taps.length ? this.formatTime(s.taps[0].tapped_at) : '-';
        var lastTap = s.taps && s.taps.length ? this.formatTime(s.taps[s.taps.length-1].tapped_at) : '-';
        var details = (s.taps || []).map(t => this.formatTime(t.tapped_at) + ' ' + this.tapLabel(t.tap_point_type) + (t.tap_point_name ? ' (' + t.tap_point_name + ')' : '')).join('; ');
        rows.push([i+1, s.name, s.admission_no, s.class_name || '', s.present ? 'Present' : 'Absent', (s.taps || []).length, firstTap, lastTap, details]);
      });
      rows.push([]);
      rows.push(['Report generated by Simitrac — simitrac.co.ke']);

      var csv = rows.map(r => r.map(c => '"' + String(c || '').replace(/"/g, '""') + '"').join(',')).join('\n');
      var blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
      var url = URL.createObjectURL(blob);
      var a = document.createElement('a'); a.href = url;
      a.download = 'Attendance_Report_' + this.dateFrom + (this.dateFrom !== this.dateTo ? '_to_' + this.dateTo : '') + '.csv';
      a.click(); URL.revokeObjectURL(url);
    },

    exportPDF() {
      var school = sessionStorage.getItem('institutionName') || 'School';
      var className = this.selectedClass ? (this.classes.find(c => c.id == this.selectedClass)?.name || 'All Classes') : 'All Classes';
      var tapTypeName = this.selectedTapType ? this.tapLabel(this.selectedTapType) : 'All Types';
      var now = new Date();
      var dateStr = now.toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' });
      var timeStr = now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: true });

      var html = '<!DOCTYPE html><html><head><title>Attendance Report — ' + school + '</title>';
      html += '<style>';
      html += '@page{size:A4;margin:15mm 20mm;}';
      html += 'body{font-family:Arial,Helvetica,sans-serif;color:#333;margin:0;padding:0;font-size:11px;line-height:1.4;}';
      // Header
      html += '.header{background:linear-gradient(135deg,#0D2B5E,#1A4B8C);color:#fff;padding:24px 30px;display:flex;justify-content:space-between;align-items:center;border-radius:0 0 8px 8px;}';
      html += '.header-left h1{margin:0;font-size:22px;font-weight:800;letter-spacing:-0.5px;}';
      html += '.header-left p{margin:3px 0 0;opacity:.8;font-size:12px;}';
      html += '.header-right{text-align:right;font-size:11px;opacity:.8;}';
      // Report title bar
      html += '.report-title{background:#f0f4f8;border-left:4px solid #0D2B5E;padding:14px 20px;margin:20px 0;border-radius:0 6px 6px 0;}';
      html += '.report-title h2{margin:0;font-size:16px;color:#0D2B5E;} .report-title p{margin:4px 0 0;color:#666;font-size:11px;}';
      // Filters
      html += '.filters{display:flex;gap:20px;margin:12px 0;font-size:11px;color:#555;flex-wrap:wrap;}';
      html += '.filter-item{background:#f8f9fa;border:1px solid #e5e7eb;padding:6px 14px;border-radius:6px;}';
      html += '.filter-item strong{color:#0D2B5E;}';
      // Stats
      html += '.stats{display:flex;gap:12px;margin:16px 0;}';
      html += '.stat{flex:1;background:#fff;border:1px solid #e5e7eb;border-radius:8px;padding:14px;text-align:center;}';
      html += '.stat-num{font-size:28px;font-weight:800;} .stat-lbl{font-size:10px;color:#888;text-transform:uppercase;letter-spacing:.5px;margin-top:2px;}';
      // Table
      html += 'table{width:100%;border-collapse:collapse;margin-top:16px;font-size:10px;}';
      html += 'th{background:#0D2B5E;color:#fff;padding:8px 10px;text-align:left;font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.3px;}';
      html += 'td{padding:7px 10px;border-bottom:1px solid #e5e7eb;vertical-align:top;}';
      html += 'tr:nth-child(even) td{background:#f9fafb;}';
      html += '.present{color:#059669;font-weight:700;} .absent{color:#DC2626;font-weight:700;}';
      html += '.tap-line{margin-bottom:2px;} .tap-dot{display:inline-block;width:6px;height:6px;border-radius:50%;margin-right:4px;vertical-align:middle;}';
      // Footer
      html += '.footer{margin-top:24px;padding-top:12px;border-top:2px solid #e5e7eb;display:flex;justify-content:space-between;font-size:9px;color:#999;}';
      html += '.footer a{color:#0D2B5E;text-decoration:none;}';
      // Confidential
      html += '.confidential{text-align:center;margin-top:8px;font-size:9px;color:#bbb;font-style:italic;}';
      // Print
      html += '@media print{body{padding:0;} .no-print{display:none !important;} .header{-webkit-print-color-adjust:exact;print-color-adjust:exact;} th{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}';
      html += '.print-bar{background:#f0f4f8;padding:12px 20px;text-align:center;position:sticky;top:0;z-index:10;}';
      html += '.print-btn{background:#0D2B5E;color:#fff;border:none;padding:10px 28px;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;margin:0 6px;}';
      html += '.print-btn:hover{background:#1A4B8C;}';
      html += '.print-btn-outline{background:#fff;color:#0D2B5E;border:2px solid #0D2B5E;padding:10px 28px;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;margin:0 6px;}';
      html += '</style></head><body>';

      // Print bar
      html += '<div class="print-bar no-print">';
      html += '<button class="print-btn" onclick="window.print()">🖨️ Print Report</button>';
      html += '<button class="print-btn-outline" onclick="window.close()">✕ Close</button>';
      html += '</div>';

      // Header
      html += '<div class="header">';
      html += '<div class="header-left"><h1>' + this.escHtml(school) + '</h1><p>Simitrac — Save Smart, Spend Smart</p></div>';
      html += '<div class="header-right">Attendance Report<br>' + dateStr + '<br>' + timeStr + '</div>';
      html += '</div>';

      // Report title
      html += '<div class="report-title"><h2>Attendance Report</h2>';
      html += '<p>Period: <strong>' + this.periodLabel + '</strong>';
      if (this.dateFrom !== this.dateTo) html += ' (' + this.dateFrom + ' to ' + this.dateTo + ')';
      else html += ' (' + this.dateFrom + ')';
      html += '</p></div>';

      // Filters
      html += '<div class="filters">';
      html += '<div class="filter-item">Class: <strong>' + this.escHtml(className) + '</strong></div>';
      html += '<div class="filter-item">Tap Type: <strong>' + this.escHtml(tapTypeName) + '</strong></div>';
      html += '<div class="filter-item">Total Students: <strong>' + this.students.length + '</strong></div>';
      html += '</div>';

      // Stats
      html += '<div class="stats">';
      html += '<div class="stat"><div class="stat-num" style="color:#059669;">' + this.presentCount + '</div><div class="stat-lbl">Present</div></div>';
      html += '<div class="stat"><div class="stat-num" style="color:#DC2626;">' + this.absentCount + '</div><div class="stat-lbl">Absent</div></div>';
      html += '<div class="stat"><div class="stat-num" style="color:#333;">' + this.totalTaps + '</div><div class="stat-lbl">Total Taps</div></div>';
      html += '<div class="stat"><div class="stat-num" style="color:#D97706;">' + this.attendanceRate + '%</div><div class="stat-lbl">Attendance Rate</div></div>';
      html += '</div>';

      // Table
      html += '<table><thead><tr><th style="width:30px;">#</th><th>Student Name</th><th>Adm No</th><th>Class</th><th>Status</th><th style="width:40px;">Taps</th><th>First In</th><th>Last Out</th><th>Tap Details</th></tr></thead><tbody>';
      this.filteredStudents.forEach((s, i) => {
        var firstTap = s.taps && s.taps.length ? this.formatTime(s.taps[0].tapped_at) : '-';
        var lastTap = s.taps && s.taps.length ? this.formatTime(s.taps[s.taps.length-1].tapped_at) : '-';
        var tapDetails = (s.taps || []).map(t => {
          var c = this.tapColor(t.tap_point_type);
          return '<div class="tap-line"><span class="tap-dot" style="background:' + c + ';"></span>' + this.formatTime(t.tapped_at) + ' — ' + this.tapLabel(t.tap_point_type) + (t.tap_point_name ? ' <span style="color:#999;">(' + this.escHtml(t.tap_point_name) + ')</span>' : '') + '</div>';
        }).join('');

        html += '<tr>';
        html += '<td style="color:#999;">' + (i + 1) + '</td>';
        html += '<td style="font-weight:600;">' + this.escHtml(s.name) + '</td>';
        html += '<td>' + this.escHtml(s.admission_no) + '</td>';
        html += '<td>' + this.escHtml(s.class_name || '-') + '</td>';
        html += '<td class="' + (s.present ? 'present' : 'absent') + '">' + (s.present ? '✓ Present' : '✗ Absent') + '</td>';
        html += '<td style="text-align:center;">' + (s.taps || []).length + '</td>';
        html += '<td>' + firstTap + '</td>';
        html += '<td>' + lastTap + '</td>';
        html += '<td>' + (tapDetails || '-') + '</td>';
        html += '</tr>';
      });
      html += '</tbody></table>';

      // Footer
      html += '<div class="footer">';
      html += '<div>Generated by <a href="https://simitrac.co.ke">Simitrac</a> on ' + dateStr + ' at ' + timeStr + '</div>';
      html += '<div>Page 1 of 1 | ' + this.escHtml(school) + '</div>';
      html += '</div>';
      html += '<div class="confidential">This report is confidential and intended for authorized school personnel only.</div>';

      html += '</body></html>';

      var w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
    },

    escHtml(str) {
      if (!str) return '';
      return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    },

    formatTime(dt) {
      if (!dt) return '-';
      var d = new Date(dt);
      return d.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', hour12: true });
    },

    getLastGps(student) {
      if (!student.taps || !student.taps.length) return null;
      for (var i = student.taps.length - 1; i >= 0; i--) {
        if (student.taps[i].gps_lat && student.taps[i].gps_lng) {
          return { lat: student.taps[i].gps_lat, lng: student.taps[i].gps_lng };
        }
      }
      return null;
    },

    showOnMap(student) {
      this.tab = 'map';
      this.$nextTick(() => {
        if (!this.mapInitialized) this.initMap();
        var gps = this.getLastGps(student);
        if (gps) {
          this.map.setView([parseFloat(gps.lat), parseFloat(gps.lng)], 17);
        }
      });
    },

    tapColor(type) {
      var colors = { gate_entry: '#10B981', gate_exit: '#EF4444', classroom: '#2E86DE', bus_boarding: '#F59E0B', bus_dropoff: '#FB923C', event: '#A855F7' };
      return colors[type] || '#8BA4C4';
    },

    tapLabel(type) {
      var labels = { gate_entry: 'Gate Entry', gate_exit: 'Gate Exit', classroom: 'Classroom', bus_boarding: 'Bus Board', bus_dropoff: 'Bus Drop', event: 'Event' };
      return labels[type] || type || 'Tap';
    },

    openGps(lat, lng) {
      window.open('https://maps.google.com/?q=' + lat + ',' + lng, '_blank');
    }
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-filter-card{padding:16px;margin-bottom:16px;}
.st-pill{background:#0A1628;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:5px 14px;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-pill.active,.st-pill:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-stat-row{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:20px;}
.st-stat{background:#0F2347;border:1px solid #1A3A6A;border-radius:12px;padding:16px 20px;flex:1;min-width:120px;}
.st-stat-num{font-size:24px;font-weight:800;color:#4FC3F7;}
.st-stat-lbl{font-size:12px;color:#8BA4C4;margin-top:2px;}
.st-tabs{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:20px;}
.st-tab{background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;border-radius:50px;padding:7px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-tab.active,.st-tab:hover{background:rgba(46,134,222,.15);border-color:#2E86DE;color:#4FC3F7;}
.st-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:24px;margin-bottom:16px;}
.st-card-title{color:#E8EDF4;font-size:16px;font-weight:700;margin-bottom:16px;}
.st-table{width:100%;border-collapse:collapse;}
.st-table th{color:#8BA4C4;font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:1px;padding:10px 12px;border-bottom:1px solid #1A3A6A;text-align:left;}
.st-table td{color:#E8EDF4;font-size:13px;padding:12px;border-bottom:1px solid #1A3A6A;}
.st-table tr:hover td{background:rgba(46,134,222,.05);}
.st-tap-dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:4px;vertical-align:middle;}
.st-view-more{background:none;border:none;color:#4FC3F7;font-size:11px;padding:2px 0;cursor:pointer;font-weight:600;}.st-view-more:hover{color:#2E86DE;text-decoration:underline;}
.st-badge{padding:3px 10px;border-radius:50px;font-size:11px;font-weight:600;}
.st-badge-success{background:rgba(16,185,129,.15);color:#10B981;}
.st-badge-warning{background:rgba(245,158,11,.15);color:#F59E0B;}
.st-badge-danger{background:rgba(239,68,68,.15);color:#EF4444;}
.st-badge-info{background:rgba(46,134,222,.15);color:#4FC3F7;}
.st-btn{border:none;border-radius:8px;padding:8px 16px;font-size:13px;font-weight:600;cursor:pointer;transition:all .2s;}
.st-btn-sm{padding:5px 12px;font-size:12px;background:#0F2347;border:1px solid #1A3A6A;color:#8BA4C4;}
.st-btn-blue{background:rgba(46,134,222,.15)!important;color:#4FC3F7!important;border-color:rgba(46,134,222,.3)!important;}
.st-btn-green{background:rgba(16,185,129,.15)!important;border-color:rgba(16,185,129,.3)!important;}
.st-btn-red{background:rgba(239,68,68,.15)!important;border-color:rgba(239,68,68,.3)!important;}
.st-empty{text-align:center;padding:40px 20px;color:#8BA4C4;}.st-empty i{font-size:48px;color:#1A3A6A;display:block;margin-bottom:12px;}
.st-spinner{display:flex;justify-content:center;padding:40px;}.st-spinner .spinner-border{color:#2E86DE;}
.st-search-wrap{position:relative;}.st-search-wrap i{position:absolute;left:12px;top:50%;transform:translateY(-50%);color:#8BA4C4;}
.st-search{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:9px 14px 9px 36px;color:#E8EDF4;font-size:14px;outline:none;width:100%;max-width:300px;}
.st-input,.st-select{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;padding:10px 14px;color:#E8EDF4;font-size:14px;outline:none;}
.st-date-input::-webkit-calendar-picker-indicator{filter:invert(0.7);cursor:pointer;}

/* Map */
.st-map{width:100%;height:400px;border-radius:12px;overflow:hidden;border:1px solid #1A3A6A;}
.st-map-legend{display:flex;flex-wrap:wrap;gap:16px;}
.st-legend-item{display:flex;align-items:center;gap:6px;color:#8BA4C4;font-size:12px;}
.st-legend-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0;}

/* Timeline */
.st-timeline-item{display:flex;gap:14px;position:relative;padding-bottom:20px;}
.st-timeline-dot{width:12px;height:12px;border-radius:50%;flex-shrink:0;margin-top:4px;z-index:1;}
.st-timeline-line{position:absolute;left:5px;top:16px;bottom:0;width:2px;background:#1A3A6A;}
.st-timeline-content{flex:1;}
.st-timeline-time{color:#4FC3F7;font-size:12px;font-weight:700;}
.st-timeline-name{color:#E8EDF4;font-size:14px;font-weight:600;margin-top:2px;}
.st-timeline-type{display:flex;align-items:center;gap:8px;margin-top:4px;}
.st-timeline-gps{color:#2E86DE;font-size:11px;cursor:pointer;font-weight:600;}
.st-timeline-gps:hover{text-decoration:underline;}

@media(max-width:768px){.st-stat-row{flex-direction:column;}.st-map{height:300px;}}
</style>